import { useForm } from "@mantine/form"
import { XCircle } from "@phosphor-icons/react"
import {
  Card,
  Checkbox,
  Flex,
  FormInput,
  FormSelector,
  MenuDivider,
  Modal,
  Note,
  RadioGroup,
  SpinnerOverlay,
  TextArea,
} from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import { pick } from "lodash-es"
import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { CustomToast } from "~/components/CustomToast"
import { TransactionHeader } from "~/components/Transaction/Header"
import { IssuanceNote } from "~/components/Transaction/IssuanceNote"
import {
  HistoryColumns,
  TransactionHistory,
} from "~/components/Transaction/TransactionHistory"
import {
  DepartmentSettlementKind,
  useAddUpdateSharedEntityMutation,
  useInvoiceSettlementDetailQuery,
  useInvoiceSettlementTransactionsQuery,
  useMapInvoiceTransactionsMutation,
} from "~/generated/graphql"
import { calculateFinalAmount } from "~/pages/pavti/lib/calculate-bifurcation-total"

const INITIAL_STATE = {
  payment_type: "full",
  amount: 0,
  attachments: [],
  remaining: 0,
}

export const InvoiceTransactionModal = ({
  historyCols = "",
  historyColSpan = "",
  columns = [
    HistoryColumns.DATE?.accessor,
    HistoryColumns.PATTI_NO?.accessor,
    HistoryColumns.PATTI_TYPE?.accessor,
    HistoryColumns.ADVANCE_TYPE?.accessor,
    HistoryColumns.PAYMENT_MODE?.accessor,
    HistoryColumns.ADVANCE_AMOUNT?.accessor,
    HistoryColumns.TRANSACTION_ID?.accessor,
    HistoryColumns.ACCOUNT_NO.accessor,
    HistoryColumns.RELEASED_AMOUNT?.accessor,
    HistoryColumns.NARRATION?.accessor,
  ],
}: // onSubmit: onSubmitProp,
{
  columns?: any[]
  historyCols?: string
  historyColSpan?: string
  // onSubmit?: () => void
}) => {
  //   const { invoiceId, showInvoice } = ;
  const [searchParams, setSearchParams] = useSearchParams()
  const advanceId = searchParams.get("advanceId")
  const id = searchParams.get("id")
  const invoiceId = searchParams.get("invoiceId")
  const transaction = searchParams.get("transaction")
  const isOpen = transaction === "true"

  const [{ data: detailData, fetching: detailFetching }] =
    useInvoiceSettlementDetailQuery({
      variables: {
        id: invoiceId as any,
      },
      pause: !isOpen || !invoiceId,
      requestPolicy: "cache-and-network",
    })

  const invoice = detailData?.invoice_by_pk as any

  const issuanceNotePendingVerification =
    invoice?.pending_issuance_note_bool?.aggregate?.count > 0

  const [, insertTransaction] = useMapInvoiceTransactionsMutation()
  const [, addUpdateSharedEntity] = useAddUpdateSharedEntityMutation()
  const form = useForm<any>({
    initialValues: INITIAL_STATE,
    validateInputOnChange: true,
    validateInputOnBlur: true,
    clearInputErrorOnChange: false,
  })

  const [{ data: invoiceTransaction }] = useInvoiceSettlementTransactionsQuery({
    variables: {
      dept: invoice?.department?.reference_id || "",
      user: invoice?.buyer?.reference_id || "",
      kind: DepartmentSettlementKind.Credit,
    },
    requestPolicy: "network-only",
  })

  const balanceTotal =
    invoiceTransaction?.transactions_for_invoice_settlement?.reduce(
      (acc, inv) => acc + (inv?.remaining_amount || 0),
      0
    )
  const transactions = invoice?.transactions

  const totalAmount = calculateFinalAmount(
    pick(
      invoice?.buyer_bifurcation,
      "deducted_amount",
      "buyer_expense_tax_amount",
      "buyer_expense_amount",
      "buyer_tax_amount"
    )
  )
  // const transactions = data?.advance_payment_by_pk?.transactions

  const paid = invoice?.buyer_bifurcation?.received_amount?.val

  const { values: state, setValues: setState } = form

  const revisedRemaining = Number(
    Number(invoice?.buyer_bifurcation?.total.toFixed(2))
  )

  useEffect(() => {
    const totalTransactionsAmount = state?.transaction_id?.reduce(
      (acc, it) => acc + it?.amount,
      0
    )
    setState(val => ({
      ...val,
      amount:
        totalTransactionsAmount >= revisedRemaining
          ? revisedRemaining
          : totalTransactionsAmount || 0,
    }))
  }, [totalAmount, revisedRemaining])

  useEffect(() => {
    setState(val => ({
      ...val,
      remaining: revisedRemaining,
    }))
  }, [transactions, revisedRemaining])

  function getInputProps(key: string) {
    const inputProps = form.getInputProps(key)
    return {
      ...inputProps,
    }
  }
  // getInputProps("")?.onChange()

  const onClose = () => {
    searchParams.delete("transaction")
    searchParams.delete("advanceId")
    searchParams.delete("invoiceId")
    searchParams.delete("id")
    setSearchParams(searchParams)
    form.reset()
  }

  const onSubmit = async () => {
    const transaction = state
    if (transaction?.amount > revisedRemaining) {
      return CustomToast({
        title: "Please enter value less than " + revisedRemaining,
        intent: "danger",
      })
    }

    const { error } = await insertTransaction({
      invoiceId: invoice?.id,
      transactions: state?.transaction_id?.map(tr => tr?.id),
      remark: state?.narration,
    })

    if (error) {
      return CustomToast({
        title: "Something went wrong! " + error.message,
        intent: "danger",
      })
    }

    onClose()
    // onSubmitProp?.()

    return CustomToast({ title: "Successfully saved transaction!" })
  }

  return (
    <Modal
      bodyClassName="max-w-7xl overflow-visible"
      isOpen={isOpen}
      onClose={onClose}
      title={"Transaction Details"}
      footerClassName={classNames(revisedRemaining === 0 ? "!hidden" : "")}
      primaryActionButtonProps={{
        text: "Yes, Confirm",
        size: "sm",
        onClick: onSubmit,
        disabled: issuanceNotePendingVerification,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        variant: "outline",
        size: "sm",
        onClick: onClose,
      }}
    >
      <Card className="!rounded-none p-2 bg-gray-100 text-gray-800 ">
        {issuanceNotePendingVerification && (
          <Note intent="danger" className="my-2 flex gap-2 items-center">
            <XCircle weight="fill" />
            <p>Issuance Note Verification Pending!</p>
          </Note>
        )}
        <SpinnerOverlay show={detailFetching} />
        <TransactionHeader
          totalAmount={totalAmount}
          seller={invoice?.buyer?.name}
          created_at={invoice?.created_at}
          id={invoice?.id}
          // kind={}
          isInvoice
          remainingAmount={revisedRemaining}
          balancePlantTransactionAmount={balanceTotal}
        />
        <IssuanceNote
          creditAmount={
            invoice?.buyer_bifurcation?.buyer_credit_note_amount?.val
          }
          debitAmount={invoice?.buyer_bifurcation?.buyer_debit_note_amount?.val}
          invoiceAmount={totalAmount}
          remaining={revisedRemaining}
          isInvoice={true}
        />
        {transactions?.length === 0 ? (
          <></>
        ) : (
          <>
            <MenuDivider />
            <TransactionHistory
              transactions={transactions}
              columns={columns}
              cols={historyCols}
              colSpan={historyColSpan}
            />
          </>
        )}
      </Card>

      <section className="p-4">
        <div className={classNames("grid grid-cols-3 gap-4")}>
          <RadioGroup
            label="Payment Type"
            required
            disabled
            {...getInputProps("payment_type")}
          >
            <Checkbox disabled type="radio" value={"full"}>
              Full Payment
            </Checkbox>
            <Checkbox disabled type="radio" value={"partial"}>
              Partial Payment
            </Checkbox>
          </RadioGroup>
          <FormInput
            {...getInputProps("amount")}
            label="Amount"
            disabled
            // disabled={application?.payment_type === "full"}
            type="number"
            required
          ></FormInput>

          <FormSelector
            // dataHook={useInvoiceSettlementTransactionsQuery}
            required
            isMulti
            label="Transaction Id"
            className={"col-span-3"}
            getOptionLabel={option => {
              return (
                <Flex className="justify-between">
                  <div className="text-gray-800">{option?.name}</div>{" "}
                  <div className="text-sm text-gray-700">
                    {inr(option?.amount)}
                  </div>
                </Flex>
              )
            }}
            options={invoiceTransaction?.transactions_for_invoice_settlement?.map(
              inv => {
                return {
                  id: inv?.id,
                  name: inv?.transaction_id,
                  amount: inv?.remaining_amount,
                }
              }
            )}
            {...getInputProps(`transaction_id`)}
            isOptionDisabled={op => state?.amount === state?.remaining}
            onChange={e => {
              setState(val => {
                const totalTransactionsAmount = e?.reduce(
                  (acc, it) => acc + it?.amount,
                  0
                )
                return {
                  ...val,
                  amount:
                    totalTransactionsAmount >= revisedRemaining
                      ? revisedRemaining
                      : totalTransactionsAmount,
                }
              })
              getInputProps(`transaction_id`)?.onChange(e)
              return
            }}
          />

          <div className="col-span-3">
            <TextArea
              className="w-full"
              label="Narration"
              {...getInputProps(`narration`)}
            />
          </div>
        </div>
      </section>
    </Modal>
  )
}
